/**
 * =============================================================================
 *  LOI TRINH DUYET CO THE SUA DUOC
 * =============================================================================
 *
 *  Hien thuc PdfCore chay tron ven trong trinh duyet:
 *    - duong DOC  di qua pdf.js  (ke thua tu PdfCoreMock)
 *    - duong GHI  di qua pdf-lib + @pdf-lib/fontkit + font Noto Sans dong goi
 *
 *  Day la loi ma nguoi dung thuc su dung cho toi khi ban WASM cua loi Rust/PDFium
 *  san sang. No khong phai ban thay the vinh vien - xem phan gioi han cuoi file.
 *
 * =============================================================================
 *  ADR-003: FILE GOC LA BAT BIEN
 * =============================================================================
 *  Byte PDF goc duoc sao mot lan luc mo va KHONG BAO GIO bi ghi vao. Moi chinh
 *  sua ton tai duoi dang mot danh sach EditCommand. Chi khi save() ta moi dung
 *  mot ban sao trong bo nho va vat chat hoa cac lenh len do.
 *
 *  Nho vay: undo/redo chi la cat bot danh sach, khong can luu anh nhi phan; hai
 *  lan save() lien tiep cho ra ket qua giong het nhau; va mot lenh loi khong the
 *  lam hong tai lieu goc.
 *
 *  LUU Y KY THUAT: pdf.js DETACH ArrayBuffer no nhan duoc. Vi vay open() phai
 *  sao byte TRUOC khi dua cho pdf.js, va phai dua cho pdf.js mot ban sao KHAC.
 *  Bo qua chi tiet nay thi originalBytes se tro thanh mang rong sau vai giay,
 *  va save() se im lang tra ve mot tep 0 byte.
 *
 * =============================================================================
 *  TU CHOI TRUNG THUC, KHONG BAO GIO LAM NGO
 * =============================================================================
 *  Rat nhieu lenh trong hop dong kieu vuot qua kha nang cua pdf-lib. Voi chung,
 *  apply() tra ve loi 'unsupported-feature' kem thong bao tieng Viet noi ro
 *  KHONG LAM DUOC GI va vi sao. Ghi nhan mot lenh roi bo qua no khi luu la dang
 *  hong te nhat: nguoi dung tin la da sua, dong tep, va mat cong.
 */
import type {
  Annotation,
  DocumentId,
  DocumentInfo,
  EditCommand,
  PdfCore,
  PdfError,
  PdfErrorCode,
  PdfRect,
  RedactionArea,
  RedactionReport,
  Result,
  SaveOptions,
  TextAlign,
  TextBlock,
} from '@pdfeditor/types';

import { PdfCoreMock } from './PdfCoreMock';
import type { CoreCapabilities, PdfJsObjectStore } from './fontDiagnostics';
import {
  loadBundledFont,
  peekBundledFont,
  normalizeFontFamilyId,
  type FontFamilyId,
} from './fonts/bundled';
import { embeddedFontSourceForBlock } from './fonts/fromPdfJs';
import {
  applyTextReplace,
  preflightTextReplace,
  type TextReplaceReport,
} from './edit/applyTextReplace';
import { alignContainerFor, parseScopeId, resolveScope, scopeAsBlock } from './edit/scope';
import {
  applyPageCommand,
  buildInitialPlan,
  foreignDocIds,
  isPageCommand,
  planIsIdentity,
  replayPagePlan,
  type PagePlan,
} from './pages/plan';
import { materializePlan } from './pages/operations';
import { replayAnnotations, isAnnotationCommand } from './annotations/replay';
import { checkAnnotation } from './annotations/support';
import { writeAnnotations } from './annotations/write';
import { ImageStore, type ImageAsset, type ImageResult } from './annotations/images';
import { applyImageContentEdits, scanPageImages } from './content/imageEdit';
import { CLIPPED_IMAGE_REASON, imageDrawOrder } from './content/imageBlocks';
import { collapseImageEdits } from './content/collapse';
import type { ImageBaseline } from './content/projection';
import {
  matchesOperatorList,
  scanPageForImages,
  type PageImageScan,
} from './content/embeddedImages';
import type { TextFaceStyle } from './fonts/resolve';
import { PDFDocument } from 'pdf-lib';

function ok<T>(value: T): Result<T> {
  return { ok: true, value };
}

function fail(code: PdfErrorCode, message: string, userMessage: string): Result<never> {
  const error: PdfError = { code, message, userMessage };
  return { ok: false, error };
}

/** Trang thai rieng cua loi nay, ben canh trang thai doc cua lop cha. */
interface EditState {
  /** Byte PDF goc. BAT BIEN - khong ham nao duoc ghi vao mang nay. */
  originalBytes: Uint8Array;
  commands: EditCommand[];
}

/**
 * Lenh ma ban trinh duyet KHONG lam duoc, kem ly do that.
 *
 * Day khong phai danh sach "chua lam kip" - moi muc deu co mot ly do ky thuat
 * cu the. pdf-lib khong co API sua content stream, nen moi thao tac cham vao
 * noi dung san co (doi kieu chu cua run cu, chen chu vao giua doan, che thong
 * tin) deu nam ngoai tam voi.
 */
const UNSUPPORTED_REASONS: Partial<Record<EditCommand['kind'], string>> = {
  'text.style':
    'Đổi cỡ chữ hoặc màu chữ của đoạn có sẵn cần sửa trực tiếp toán tử trong content stream. ' +
    'Bản web vẽ đè chứ không sửa được toán tử, nên thao tác này cần lõi Rust/PDFium.',
  'text.insert':
    'Chèn chữ tại một điểm bất kỳ cần biết dòng chảy văn bản của trang để không đè lên nội dung ' +
    'sẵn có. Bản web chỉ sửa được trong phạm vi một khối đã nhận dạng (ADR-005).',
  // Ba lenh chu thich (annotation.add / annotation.update / annotation.remove)
  // TRUOC DAY nam trong danh sach nay voi ly do "cần lõi Rust/PDFium". Ly do do
  // SAI: /Annots la mot cay dictionary thuong, khong phai content stream, va
  // pdf-lib dung duoc no bang API cap thap. Chung da duoc hien thuc that - xem
  // core/annotations/. Chu thich ghi ra la doi tuong /Annot dung dac ta, co
  // dong hinh dang /AP, va Acrobat mo ra sua lai duoc.
  //
  // Hai loai VAN chua ghi duoc ('free-text' va 'stamp') bi tu choi rieng tung
  // cai kem ly do ky thuat - xem annotations/support.ts UNWRITABLE_REASON.
  // Bon lenh cap trang (page.insert / page.remove / page.reorder / page.rotate)
  // TRUOC DAY nam trong danh sach nay. Chung da duoc hien thuc that bang
  // pdf-lib - xem core/pages/. pdf-lib sao chep trang, dat /Rotate va ghi lai
  // ca tep tot; khac voi sua content stream, day khong phai viec phai doi lo
  // Rust/PDFium. Cai duy nhat mat di la cau truc cap tai lieu (bookmark, lien
  // ket noi bo, cay the), va giao dien noi ro dieu do - xem
  // PAGE_OPS_FIDELITY_WARNING.
  // Anh CHEN DUOC roi, nhung KHONG di qua lenh nay. Anh duoc ghi thanh mot chu
  // thich /Stamp (annotation.add voi kind 'stamp'), nen no chon duoc, di chuyen
  // duoc, xoa duoc va hoan tac duoc y nhu moi doi tuong khac - trong khi
  // 'image.insert' se ve chet anh vao content stream cua trang. Xem
  // core/annotations/images.ts.
  'image.insert':
    'Ảnh được chèn dưới dạng chú thích /Stamp (lệnh annotation.add) chứ không vẽ chết vào nội ' +
    'dung trang, để người dùng còn chọn, di chuyển và xóa lại được. Lệnh image.insert vẽ trực ' +
    'tiếp vào content stream nên bản web không dùng đến nó.',
  'redact.apply':
    'Che thông tin TUYỆT ĐỐI không được làm bằng đường vẽ đè của bản web. Vẽ đè không xóa glyph: ' +
    'chữ gốc vẫn nằm nguyên trong tệp và vẫn sao chép ra được. Một bản "che" nửa vời nguy hiểm hơn ' +
    'là không che, vì nó tạo cảm giác an toàn sai lầm. Chức năng này chỉ có ở lõi Rust/PDFium.',
};

// ---------------------------------------------------------------------------

export class PdfCoreBrowser extends PdfCoreMock implements PdfCore {
  override readonly kind = 'browser-pdflib' as const;

  private readonly edits = new Map<string, EditState>();

  /**
   * BYTE CUA CAC TAI LIEU NGOAI da nhap de ghep.
   *
   * Co pham vi theo CA LOI chu khong theo tung tai lieu, va do la co y: duong
   * luu mo mot tai lieu lam viec MOI tu byte goc roi phat lai ca danh sach lenh
   * len do (xem App.handleSave). Neu kho nay gan vao tai lieu dang xem thi tai
   * lieu lam viec moi se khong tim thay nguon, va lenh ghep se that bai dung
   * vao luc nguoi dung bam Luu.
   */
  private readonly sources = new Map<string, { bytes: Uint8Array; label: string; pageCount: number }>();

  private sourceCounter = 0;

  /**
   * Ket qua quet anh theo trang. Giu ca Promise chu khong giu ket qua, cung ly
   * do voi `sourceViews`: mot trang co the bi hoi tu nhieu cho trong cung mot
   * khung hinh, va getOperatorList() la thao tac phan tich lai ca trang.
   */
  private readonly imageScans = new Map<string, Promise<Result<PageImagesInfo>>>();

  /**
   * BYTE CUA CAC ANH da chen. Pham vi theo CA LOI, cung ly do voi `sources`:
   * duong luu mo mot tai lieu lam viec moi tu byte goc roi phat lai danh sach
   * lenh len do, va luc do no phai tim duoc byte anh.
   *
   * Chu thich chi mang MA anh (`Annotation.imageRef`). Xem hop dong kieu.
   */
  private readonly images = new ImageStore();

  /**
   * Tai lieu nguon DA MO de hien thi, theo ma nguon.
   *
   * Giu cai Promise chu khong giu ket qua: khung xem hoi cung mot ma nguon tu
   * nhieu o trang gan nhu cung luc (mot tep ghep 40 trang la 40 lan hoi trong
   * cung mot khung hinh). Giu ket qua thi ca 40 lan deu thay "chua co" va mo ra
   * 40 worker pdf.js cho cung mot tep - het bo nho ngay lap tuc.
   */
  private readonly sourceViews = new Map<
    string,
    Promise<Result<{ doc: DocumentId; info: DocumentInfo }>>
  >();

  /**
   * Loi nay CO font du phong. Do la khac biet duy nhat ve chan doan font so voi
   * ban gia lap, va no la khac biet quyet dinh: mot khoi ma ban gia lap xep
   * 'none' hoac 'style-only' thi o day thanh 'resubset' - sua duoc that.
   */
  protected override capabilities(): CoreCapabilities {
    return { bundledFontAvailable: peekBundledFont() !== null };
  }

  override async open(bytes: Uint8Array, password?: string): Promise<Result<DocumentId>> {
    // Hai ban sao, co chu dich. pdf.js detach buffer no nhan duoc; neu ta dua
    // thang `bytes` cho no thi `bytes` (va moi ban sao dung chung buffer) tro
    // thanh rong ngay sau do. Xem ghi chu ADR-003 o dau file.
    const original = bytes.slice();
    const forPdfjs = bytes.slice();

    const opened = await super.open(forPdfjs, password);
    if (!opened.ok) return opened;

    this.edits.set(opened.value.id, { originalBytes: original, commands: [] });

    // Nap font dong goi ngay khi mo tai lieu: chan doan font cua trang dau tien
    // se duoc yeu cau chi vai chuc mili giay sau, va no can biet font co san hay
    // khong. Khong chan viec mo tep neu tai that bai.
    void loadBundledFont();

    return opened;
  }

  override async close(doc: DocumentId): Promise<void> {
    this.edits.delete(doc.id);
    // Bo nho dem quet anh CO KHOA THEO doc.id. Khong don o day thi mo lai cung
    // mot tep se lay lai ket qua quet cua lan truoc neu ma tai lieu trung nhau.
    for (const key of [...this.imageScans.keys()]) {
      if (key.startsWith(doc.id + ':')) this.imageScans.delete(key);
    }
    await super.close(doc);
  }

  /**
   * Bao dam font dong goi da duoc nap TRUOC khi chan doan font chay, de
   * capabilities() tra loi dung. Neu khong, trang dau tien se bi bao "khong sua
   * duoc" chi vi cuoc goi mang chua kip ve.
   */
  override async textBlocks(doc: DocumentId, pageIndex: number): Promise<Result<TextBlock[]>> {
    await loadBundledFont();
    return super.textBlocks(doc, pageIndex);
  }

  // --- Duong GHI -----------------------------------------------------------

  /**
   * Kiem tra mot lenh va ghi nhan no. KHONG ghi gi vao byte goc.
   *
   * Kiem tra ngay tai day (chu khong doi den save) la co chu dich: nguoi dung
   * phai biet lenh bi tu choi VAO LUC HO BAM, chu khong phai muoi phut sau khi
   * bam Luu.
   */
  override async apply(doc: DocumentId, command: EditCommand): Promise<Result<void>> {
    const state = this.edits.get(doc.id);
    if (!state) {
      return fail('io-error', 'unknown document id', 'Tài liệu không còn mở. Vui lòng mở lại tệp.');
    }

    if (isPageCommand(command)) {
      // Kiem tra bang cach AP THU len so do dang co. Chi so trang la thu phu
      // thuoc vi tri: mot lenh "xóa trang 7" hop le hom nay co the vo nghia sau
      // khi nguoi dung xoa bot trang, va no phai bi chan NGAY chu khong phai o
      // buoc vat chat hoa.
      const planNow = await this.currentPlan(doc, state);
      if (!planNow.ok) return planNow;

      if (command.kind === 'page.insert' && command.source.kind === 'document') {
        const source = this.sources.get(command.source.docId);
        if (!source) {
          return fail(
            'io-error',
            `unknown source document ${command.source.docId}`,
            'Tệp nguồn để ghép không còn nữa. Hãy chọn lại tệp cần ghép.',
          );
        }
        if (command.source.pageIndex < 0 || command.source.pageIndex >= source.pageCount) {
          return fail(
            'page-out-of-range',
            `source page ${command.source.pageIndex} out of range in ${command.source.docId}`,
            `Tệp “${source.label}” chỉ có ${source.pageCount} trang.`,
          );
        }
      }

      const stepped = applyPageCommand(planNow.value, command);
      if (!stepped.ok) {
        return fail('page-out-of-range', `page command refused: ${stepped.error}`, stepped.error);
      }

      state.commands.push(command);
      return ok(undefined);
    }

    if (isAnnotationCommand(command)) {
      const checked = await this.checkAnnotationCommand(doc, state, command);
      if (!checked.ok) return checked;
      state.commands.push(command);
      return ok(undefined);
    }

    if (command.kind === 'content.image.transform' || command.kind === 'content.image.remove') {
      const checked = await this.checkImageCommand(
        state,
        command.pageIndex,
        command.occurrence,
        command.kind === 'content.image.transform',
      );
      if (!checked.ok) return checked;
      state.commands.push(command);
      return ok(undefined);
    }

    if (command.kind !== 'text.replace') {
      const why = UNSUPPORTED_REASONS[command.kind] ?? 'Thao tác này chưa có ở bản web.';
      return fail(
        'unsupported-feature',
        `browser core cannot perform ${command.kind}`,
        `Bản web chưa làm được thao tác "${command.kind}". ${why}`,
      );
    }

    // `command.align` co the vang mat - luc do duong ghi SUY RA, dung nhu truoc
    // khi hop dong co truong nay. Co mat thi no phai duoc dung ngay tu buoc kiem
    // tra: mot lan sua vua khoi khi can trai co the KHONG vua khi can phai
    // (mep phai cua trang), va nguoi dung phai biet dieu do luc bam chu khong
    // phai luc luu.
    const prepared = await this.prepareTextReplace(doc, command.blockId, command.newText, {
      ...(command.align ? { align: command.align } : {}),
      ...(command.bold || command.italic
        ? { face: { bold: command.bold === true, italic: command.italic === true } }
        : {}),
      ...(command.fontFamily ? { family: normalizeFontFamilyId(command.fontFamily) } : {}),
      ...(command.fontSizePt !== undefined ? { fontSizePt: command.fontSizePt } : {}),
      ...(command.cover ? { cover: true } : {}),
    });
    if (!prepared.ok) return prepared;

    const report = prepared.value;
    if (!report.ok) {
      return fail(
        report.errorCode ?? 'unsupported-feature',
        `text.replace refused: ${report.outcome}`,
        report.reason,
      );
    }

    state.commands.push(command);
    return ok(undefined);
  }

  /**
   * Vat chat hoa danh sach lenh len MOT BAN SAO cua byte goc.
   *
   * Cac lenh duoc ap tuan tu, dau ra cua lenh truoc la dau vao cua lenh sau.
   * Hinh hoc cua khoi luon lay tu TAI LIEU GOC: ta ve de len chu khong xoa, nen
   * khoi cu van nam nguyen cho cu qua moi lan ap.
   */
  override async save(doc: DocumentId, options?: SaveOptions): Promise<Result<Uint8Array>> {
    const state = this.edits.get(doc.id);
    if (!state) {
      return fail('io-error', 'unknown document id', 'Tài liệu không còn mở. Vui lòng mở lại tệp.');
    }

    if (options?.incremental === true) {
      // Ghi tang truong noi them ban sua doi va GIU LAI phien ban object cu.
      // pdf-lib khong lam duoc kieu ghi do, va lam nua voi thi con te hon.
      return fail(
        'unsupported-feature',
        'incremental save not supported by pdf-lib write path',
        'Bản web chỉ ghi lại toàn bộ tệp, chưa hỗ trợ ghi tăng trưởng. Chữ ký số có sẵn trong ' +
          'tài liệu sẽ mất hiệu lực sau khi lưu.',
      );
    }
    if (options?.encryption) {
      return fail(
        'unsupported-feature',
        'encryption on save not supported by pdf-lib write path',
        'Bản web chưa đặt được mật khẩu hay mã hóa khi lưu. Thao tác này cần lõi Rust/PDFium.',
      );
    }

    // Khong co lenh nao: tra ve mot BAN SAO cua byte goc. Tra thang mang goc se
    // giao quyen ghi vao byte bat bien cho ben goi.
    if (state.commands.length === 0) {
      return ok(state.originalBytes.slice());
    }

    // =======================================================================
    //  GIAI DOAN 1 - SUA CHU, tren khong gian chi so trang CUA TEP GOC
    // =======================================================================
    //  Ma khoi ("p3-b7") mang theo chi so trang cua tai lieu DANG MO, va tai
    //  lieu dang mo khong bao gio bi mo lai sau mot thao tac trang - thao tac
    //  trang chi song trong so do. Nen sua chu PHAI chay TRUOC, khi chi so trang
    //  con dung nghia goc cua no. Dao thu tu hai giai doan nay se ve chu len
    //  nham trang ngay khi nguoi dung vua xoa mot trang phia truoc.
    let current: Uint8Array = state.originalBytes.slice();

    // =======================================================================
    //  GIAI DOAN 0 - PHAU THUAT ANH TRONG CONTENT STREAM
    // =======================================================================
    //  PHAI chay TRUOC sua chu, va thu tu nay khong duoc dao.
    //
    //  Sua chu THEM mot luong noi dung moi vao trang (pdf-lib pushOperators).
    //  Phau thuat anh thi DOC lai toan bo /Contents, sua tren day byte da noi,
    //  roi ghi tra ve mot luong duy nhat. Chay sau, no se phai doc ca phan chu
    //  moi vua them - vo hai nhung vo ich - va quan trong hon: so thu tu toan
    //  tu `Do` ma giao dien ghi vao lenh duoc dem tren luong GOC. Bat cu thu gi
    //  chen vao truoc do deu la mot co hoi cho hai he dem lech nhau.
    const imageEdits = collapseImageEdits(state.commands);
    for (const [pageIndex, edits] of imageEdits) {
      const done = await applyImageContentEdits(current, pageIndex, edits);
      if (!done.ok) {
        return fail(
          'engine-panic',
          `image content edit failed on page ${pageIndex}`,
          `Không lưu được thay đổi ảnh ở trang ${pageIndex + 1}: ${done.reason}`,
        );
      }
      current = done.bytes;
    }

    for (const command of state.commands) {
      if (command.kind !== 'text.replace') continue; // apply() da chan tu truoc
      const prepared = await this.prepareTextReplace(doc, command.blockId, command.newText, {
        ...(command.align ? { align: command.align } : {}),
        ...(command.bold || command.italic
          ? { face: { bold: command.bold === true, italic: command.italic === true } }
          : {}),
        ...(command.fontFamily ? { family: normalizeFontFamilyId(command.fontFamily) } : {}),
        ...(command.fontSizePt !== undefined ? { fontSizePt: command.fontSizePt } : {}),
        ...(command.cover ? { cover: true } : {}),
      });
      if (!prepared.ok) return prepared;

      const written = await applyTextReplace(current, {
        pageIndex: prepared.value.pageIndex,
        block: prepared.value.block,
        newText: command.newText,
        fontSource: prepared.value.fontSource,
        ...(command.bold || command.italic
          ? { face: { bold: command.bold === true, italic: command.italic === true } }
          : {}),
        // HO FONT nguoi dung chon di ra tan duong ghi. Doc thieu no o day chinh
        // la cai lam ban xem truoc mot phong ma tep xuat ra mot phong khac.
        ...(command.fontFamily ? { family: normalizeFontFamilyId(command.fontFamily) } : {}),
        // CO CHU nguoi dung chon cung di ra tan duong ghi, cung ly do: doc thieu
        // no thi ban xem truoc mot co chu ma tep xuat ra mot co khac.
        ...(command.fontSizePt !== undefined ? { fontSizePt: command.fontSizePt } : {}),
        ...(command.cover ? { cover: true } : {}),
        // LUA CHON CUA NGUOI DUNG DI RA TOI DAY, hoac khong co gi va duong ghi
        // suy ra. Doc thieu truong nay chinh la cai lam cho ban xem truoc can
        // giua ma tep xuat ra can trai - xem coreAcceptsAlign() trong
        // ui/textEditCore.ts.
        ...(command.align ? { align: command.align } : {}),
        alignContainer: prepared.value.alignContainer,
      });
      if (!written.ok || !written.bytes) {
        return fail(
          written.errorCode ?? 'engine-panic',
          `save failed at ${command.kind} on ${command.blockId}: ${written.outcome}`,
          `Không lưu được thay đổi ở khối "${command.blockId}": ${written.reason}`,
        );
      }
      current = written.bytes;
    }

    // =======================================================================
    //  GIAI DOAN 2 - THAO TAC TRANG, dung MOT LAN tu so do
    // =======================================================================
    //  Danh sach lenh KHONG duoc ap tung lenh len byte. No duoc phat lai thanh
    //  mot so do trang (ham thuan tuy), va tai lieu ket qua duoc dung DUNG MOT
    //  LAN tu so do do.
    //
    //  Day la thu lam cho "luu hai lan cho ra hai tep giong het nhau" thanh
    //  dung theo cau truc chu khong phai nho cong. save() bat dau lai tu
    //  state.originalBytes o moi lan goi, va replayPagePlan la ham thuan tuy
    //  tren cung mot danh sach lenh - nen khong co duong nao de mot lenh duoc
    //  ap hai lan.
    const planResult = await this.currentPlan(doc, state);
    if (!planResult.ok) return planResult;
    const plan = planResult.value;

    const infoForPlan = await this.info(doc);
    if (!infoForPlan.ok) return infoForPlan;
    const identityPlan = planIsIdentity(plan, infoForPlan.value.pageCount);

    if (!identityPlan) {
      const sources = new Map<string, Uint8Array>();
      for (const docId of foreignDocIds(plan)) {
        const entry = this.sources.get(docId);
        if (!entry) {
          return fail(
            'io-error',
            `missing source bytes for ${docId} at save time`,
            'Một tệp dùng để ghép không còn trong bộ nhớ. Hãy chọn lại tệp đó rồi ghép lại.',
          );
        }
        sources.set(docId, entry.bytes);
      }

      try {
        current = await materializePlan(plan, { originalBytes: current, sources });
      } catch (err) {
        return fail(
          'engine-panic',
          `page plan materialization failed: ${String(err)}`,
          `Không dựng được tệp sau khi sắp xếp trang: ${String(err).slice(0, 160)}`,
        );
      }
    }

    // =======================================================================
    //  GIAI DOAN 3 - CHU THICH, tren khong gian chi so trang CUA TEP DICH
    // =======================================================================
    //  PHAI chay SAU giai doan 2, va thu tu nay khong duoc dao.
    //
    //  Chu thich duoc gan vao trang cua TEP KET QUA. Neu gan truoc khi vat chat
    //  hoa, copyPages() se mang chung theo trang - nghe co ve dung, cho den khi
    //  mot trang xuat hien HAI LAN trong so do: luc do chu thich bi nhan doi
    //  ngoai y muon, va mot trang bi xoa lai keo theo chu thich cua no bien mat
    //  mot cach tinh co chu khong phai theo mot quy tac ta kiem soat duoc.
    //
    //  Dat o day thi quy tac la TUONG MINH va kiem chung duoc: placeAnnotations()
    //  chieu chi so trang goc sang chi so trang dich qua chinh so do.
    const annotations = replayAnnotations(state.commands);
    if (annotations.length === 0) return ok(current);

    let written;
    try {
      written = await writeAnnotations(current, annotations, {
        plan: identityPlan ? undefined : plan,
        images: this.images.snapshot(),
      });
    } catch (err) {
      return fail(
        'engine-panic',
        `annotation write failed: ${String(err)}`,
        `Không ghi được chú thích vào tệp: ${String(err).slice(0, 160)}`,
      );
    }

    // KHONG BAO GIO im lang bo qua mot chu thich. apply() da chan moi lenh
    // khong hop le tu truoc, nen den day ma con bi loai thi do la mot lo hong
    // giua hai cua kiem - phai bao ra chu khong duoc luu mot tep thieu dau.
    if (written.report.skipped.length > 0) {
      const first = written.report.skipped[0] as { annotation: Annotation; reason: string };
      return fail(
        'engine-panic',
        `annotation ${first.annotation.id} rejected at write time: ${first.reason}`,
        `Không ghi được ${written.report.skipped.length} chú thích vào tệp. ` +
          `Lý do đầu tiên: ${first.reason}`,
      );
    }

    return ok(written.bytes);
  }

  // --- Chu thich ------------------------------------------------------------

  /**
   * Kiem tra mot lenh chu thich TAI LUC NGUOI DUNG BAM.
   *
   * Cua kiem la checkAnnotation() trong annotations/support.ts - DUNG mot ham
   * ma duong ghi goi. Neu cho nay noi "được" ma duong ghi noi "không", nguoi
   * dung se mat dau cua ho o buoc Luu, tuc la dung che do hong ma ca tinh nang
   * nay sinh ra de go bo.
   */
  private async checkAnnotationCommand(
    doc: DocumentId,
    state: EditState,
    command: EditCommand,
  ): Promise<Result<void>> {
    const infoResult = await this.info(doc);
    if (!infoResult.ok) return infoResult;
    const pageCount = infoResult.value.pageCount;

    if (command.kind === 'annotation.add') {
      const checked = checkAnnotation(command.annotation, pageCount);
      if (!checked.ok) {
        return fail(
          checked.code,
          `annotation.add refused: ${command.annotation.kind}`,
          checked.reason,
        );
      }
      return ok(undefined);
    }

    // 'update' va 'remove' tro toi mot doi tuong DANG CO. Ma khong con ton tai
    // phai bi tu choi chu khong duoc lang le bo qua: mot lenh bi bo qua van nam
    // trong danh sach va se duoc phat lai o lan luu sau.
    if (command.kind !== 'annotation.update' && command.kind !== 'annotation.remove') {
      return fail('unsupported-feature', `not an annotation command: ${command.kind}`, 'Lệnh không hợp lệ.');
    }
    const current = replayAnnotations(state.commands);
    const id = command.id;
    const existing = current.find((a) => a.id === id);
    if (!existing) {
      return fail(
        'unsupported-feature',
        `annotation ${id} not found`,
        'Chú thích này không còn nữa — có thể đã bị xóa hoặc đã hoàn tác.',
      );
    }

    if (command.kind === 'annotation.update') {
      // Kiem tra KET QUA SAU KHI VA, khong phai kiem ban va. Mot lenh doi mau
      // sang gan trang phai bi chan o day, va chi ban da va moi tra loi duoc.
      const patched = { ...existing, ...command.patch, id: existing.id };
      const checked = checkAnnotation(patched, pageCount);
      if (!checked.ok) {
        return fail(checked.code, `annotation.update refused: ${id}`, checked.reason);
      }
    }

    return ok(undefined);
  }

  // --- Anh chen vao ---------------------------------------------------------

  /**
   * GHI NHAN mot tep anh, sau khi da GIAI MA THAT no.
   *
   * Goi ham nay TRUOC khi phat lenh 'annotation.add' voi kind 'stamp'. No tra ve
   * ma tra cuu de dat vao `Annotation.imageRef`, kem kich thuoc tu nhien va so
   * byte anh se them vao tep - de giao dien noi cho nguoi dung biet gia phai tra
   * TRUOC khi ho chen, chu khong phai sau khi tep da phinh len ba chuc megabyte.
   *
   * Tu choi cua ham nay la tu choi CUOI CUNG: cai gi qua duoc day thi qua duoc
   * ca duong luu, vi ca hai cho chay cung bo giai ma cua pdf-lib.
   */
  async registerImage(bytes: Uint8Array, label: string): Promise<ImageResult> {
    return this.images.add(bytes, label);
  }

  /** Anh da ghi nhan - giao dien can no de ve len man hinh va do kich thuoc. */
  imageAsset(ref: string): ImageAsset | undefined {
    return this.images.get(ref);
  }

  /** Chu thich dang co hieu luc - giao dien dung de doi chieu voi ban tren man hinh. */
  annotations(doc: DocumentId): Annotation[] {
    const state = this.edits.get(doc.id);
    if (!state) return [];
    return replayAnnotations(state.commands);
  }

  // --- Thao tac trang -------------------------------------------------------

  /** So do trang dang co hieu luc, phat lai tu so trang goc + danh sach lenh. */
  private async currentPlan(doc: DocumentId, state: EditState): Promise<Result<PagePlan>> {
    const infoResult = await this.info(doc);
    if (!infoResult.ok) return infoResult;
    const replayed = replayPagePlan(
      buildInitialPlan(infoResult.value.pageCount),
      state.commands,
    );
    if (!replayed.ok) {
      return fail('page-out-of-range', `page plan replay failed: ${replayed.error}`, replayed.error);
    }
    return ok(replayed.plan);
  }

  /** So do trang dang co hieu luc - cho giao dien doi chieu voi thanh ben. */
  async pagePlan(doc: DocumentId): Promise<Result<PagePlan>> {
    const state = this.edits.get(doc.id);
    if (!state) {
      return fail('io-error', 'unknown document id', 'Tài liệu không còn mở. Vui lòng mở lại tệp.');
    }
    return this.currentPlan(doc, state);
  }

  /**
   * Ghi nhan mot tai lieu NGOAI de ghep vao, sau khi da kiem tra no phan tich duoc.
   *
   * Day la cua kiem tra duy nhat cho byte tu ben ngoai. Goi ham nay TRUOC khi
   * phat bat ky lenh 'page.insert' nao tro toi tep do: mot tep hong phai bi
   * chan o day, chu khong duoc lam hong ca tai lieu dang lam viec o buoc luu.
   */
  async registerSourceDocument(
    bytes: Uint8Array,
    label: string,
  ): Promise<Result<{ docId: string; label: string; pageCount: number }>> {
    let pageCount: number;
    try {
      const probe = await PDFDocument.load(bytes.slice(), {
        ignoreEncryption: true,
        updateMetadata: false,
      });
      pageCount = probe.getPageCount();
    } catch (err) {
      return fail(
        'parse-failed',
        `merge source failed to parse: ${String(err)}`,
        `Không đọc được tệp “${label}”. Tệp có thể hỏng, bị mã hóa, hoặc không phải PDF.`,
      );
    }

    if (pageCount === 0) {
      return fail('parse-failed', 'merge source has zero pages', `Tệp “${label}” không có trang nào.`);
    }

    this.sourceCounter += 1;
    const docId = `src-${this.sourceCounter}`;
    this.sources.set(docId, { bytes: bytes.slice(), label, pageCount });
    return ok({ docId, label, pageCount });
  }

  /**
   * Mo mot tai lieu nguon de HIEN THI, va giu no mo.
   *
   * Khung xem can chinh doi tuong trang cua pdf.js thuoc tai lieu NAY de ve o
   * trang ghep; khong co duong nao lay trang cua tep B tu tai lieu cua tep A.
   *
   * Ket qua duoc giu lai theo `docId`: mot tep ghep 40 trang se hoi 40 lan
   * trong cung mot khung hinh, va moi lan mo la mot worker pdf.js nua.
   */
  async openSourceForView(
    docId: string,
  ): Promise<Result<{ doc: DocumentId; info: DocumentInfo }>> {
    const entry = this.sources.get(docId);
    if (!entry) {
      return fail(
        'io-error',
        `unknown source document ${docId}`,
        'Tệp nguồn để ghép không còn nữa. Hãy chọn lại tệp cần ghép.',
      );
    }

    const cached = this.sourceViews.get(docId);
    if (cached) return cached;

    const pending = (async (): Promise<Result<{ doc: DocumentId; info: DocumentInfo }>> => {
      const opened = await this.open(entry.bytes.slice());
      if (!opened.ok) return opened;
      const info = await this.info(opened.value);
      if (!info.ok) {
        await this.close(opened.value);
        return info;
      }
      return ok({ doc: opened.value, info: info.value });
    })();

    this.sourceViews.set(docId, pending);

    // Mot lan mo THAT BAI khong duoc dong bang vinh vien: giu lai ket qua loi
    // se lam moi lan ve sau tra ve dung cai loi do ma khong bao gio thu lai.
    const settled = await pending;
    if (!settled.ok) this.sourceViews.delete(docId);
    return settled;
  }

  /** Dong toan bo tai lieu nguon dang mo de hien thi. Byte thi van giu lai. */
  async closeSourceViews(): Promise<void> {
    const views = [...this.sourceViews.values()];
    this.sourceViews.clear();
    for (const view of views) {
      const settled = await view.catch(() => null);
      if (settled && settled.ok) await this.close(settled.value.doc);
    }
  }

  /** Nhan cua mot tai lieu nguon da ghi nhan - de hien trong thanh ben. */
  sourceLabel(docId: string): string | null {
    return this.sources.get(docId)?.label ?? null;
  }

  /**
   * Che thong tin: TU CHOI HAN, va day la mot quyet dinh ve an toan chu khong
   * phai ve cong suc.
   *
   * Duong sua cua ban web VE DE LEN chu khong xoa glyph. Neu ta cho no gia lam
   * chuc nang che thong tin, ket qua se la mot tai lieu trong nhu da che ma noi
   * dung goc van sao chep ra duoc nguyen ven. Do la mau ro ri du lieu da xay ra
   * nhieu lan trong thuc te.
   */
  override async applyRedactions(
    _doc: DocumentId,
    _areas: RedactionArea[],
  ): Promise<Result<RedactionReport>> {
    return fail(
      'unsupported-feature',
      'redaction must never be emulated by the draw-over write path',
      'Bản web KHÔNG che thông tin được. Cách sửa của bản web là vẽ đè, không xóa chữ gốc: ' +
        'chữ vẫn nằm nguyên trong tệp và vẫn sao chép ra được. Một bản "che" nửa vời nguy hiểm ' +
        'hơn là không che. Hãy dùng bản desktop với lõi Rust/PDFium để che thông tin.',
    );
  }

  // --- Noi bo ---------------------------------------------------------------

  /**
   * Tim DON VI duoc chon, dung ban ghi font nguon, va chay TOAN BO cua chan.
   * Dung chung cho apply() (kiem tra som) va save() (thuc hien that).
   *
   * `scopeId` co the tro toi ca khoi ("p0-b3"), mot dong ("p0-b3#l2") hay mot
   * run ("p0-b3#r5"). Tu day tro xuong, moi thu deu chay tren TextBlock TONG HOP
   * cua rieng don vi do: bbox cua no, run cua no, duong co so cua no. Duong ghi
   * khong con nhin thay khoi cha nua - do la ca muc dich cua viec nay.
   */
  private async prepareTextReplace(
    doc: DocumentId,
    scopeId: string,
    newText: string,
    /**
     * Lua chon cua nguoi dung. MOI truong o day deu phai di theo den tan day:
     * neu apply() kiem bang mot bo lua chon con save() ghi bang mot bo khac thi
     * hai buoc dang tra loi hai cau hoi khac nhau, va nguoi dung tin vao cau
     * tra loi cua buoc dau.
     *
     * `align` bo qua = SUY RA tu chu cu, khong phai = 'left'.
     * `face` bo qua = giu mat chu thuong.
     * `family` bo qua = ho font mac dinh (noto-sans).
     * `fontSizePt` bo qua = giu co chu do duoc tu chu cu.
     * `cover` = che chu, khong ve chu moi.
     */
    options: {
      align?: TextAlign;
      face?: TextFaceStyle;
      family?: FontFamilyId;
      fontSizePt?: number;
      cover?: boolean;
    } = {},
  ): Promise<
    Result<
      TextReplaceReport & {
        pageIndex: number;
        block: TextBlock;
        fontSource: ReturnType<typeof embeddedFontSourceForBlock>;
        /** Hop chua pham vi - duong ghi phai dung LAI dung hop nay de do can le. */
        alignContainer: PdfRect;
      }
    >
  > {
    const { blockId } = parseScopeId(scopeId);
    const pageIndex = pageIndexFromBlockId(blockId);
    if (pageIndex === null) {
      return fail(
        'unsupported-feature',
        `cannot derive page index from scope id "${scopeId}"`,
        `Không xác định được khối văn bản "${scopeId}" thuộc trang nào.`,
      );
    }

    const infoResult = await this.info(doc);
    if (!infoResult.ok) return infoResult;
    const pageInfo = infoResult.value.pages[pageIndex];
    if (!pageInfo) {
      return fail(
        'page-out-of-range',
        `page ${pageIndex} out of range`,
        `Trang ${pageIndex + 1} không tồn tại trong tài liệu này.`,
      );
    }

    const blocksResult = await this.textBlocks(doc, pageIndex);
    if (!blocksResult.ok) return blocksResult;
    const parent = blocksResult.value.find((b) => b.id === blockId);
    if (!parent) {
      return fail(
        'unsupported-feature',
        `block ${blockId} not found`,
        `Không tìm thấy khối văn bản "${blockId}" nữa. Tài liệu có thể đã được phân tích lại; ` +
          'hãy chọn lại khối cần sửa.',
      );
    }

    // Ma pham vi tro toi mot don vi khong con ton tai (tai lieu duoc phan tich
    // lai, so run doi) phai TU CHOI, tuyet doi khong duoc lang le lui ve ca khoi:
    // lui ve khoi tuc la ve de len ca bang - dung cai loi ma viec chon theo pham
    // vi sinh ra de tranh.
    const scope = resolveScope(parent, scopeId);
    if (!scope) {
      return fail(
        'unsupported-feature',
        `scope ${scopeId} could not be resolved inside ${blockId}`,
        `Không tìm thấy phần văn bản "${scopeId}" nữa. Tài liệu có thể đã được phân tích lại; ` +
          'hãy chọn lại phần cần sửa.',
      );
    }
    const block = scopeAsBlock(parent, scope);
    // Tinh TAI DAY, noi duy nhat con nhin thay ca khoi CHA lan pham vi. Sau
    // scopeAsBlock() thi khoi cha bien mat khoi duong ghi - va do la co y (xem
    // scope.ts) - nen hop de do can le phai duoc chuyen tiep tuong minh.
    const alignContainer = alignContainerFor(parent, scope);

    const proxy = await this.getPageProxy(doc, pageIndex);
    const fontSource = proxy
      ? embeddedFontSourceForBlock(proxy.commonObjs as unknown as PdfJsObjectStore, block)
      : null;

    const { report } = await preflightTextReplace({
      block,
      newText,
      fontSource,
      page: {
        widthPt: pageInfo.widthPt,
        heightPt: pageInfo.heightPt,
        rotation: pageInfo.rotation,
      },
      alignContainer,
      ...(options.align ? { align: options.align } : {}),
      ...(options.face ? { face: options.face } : {}),
      ...(options.family ? { family: options.family } : {}),
      ...(options.fontSizePt !== undefined ? { fontSizePt: options.fontSizePt } : {}),
      ...(options.cover ? { cover: true } : {}),
    });

    return ok({ ...report, pageIndex, block, fontSource, alignContainer });
  }

  // --- Truy van phu tro cho giao dien --------------------------------------

  /** So lenh dang cho luu. Giao dien dung de bat/tat nut Luu. */
  pendingCommandCount(doc: DocumentId): number {
    return this.edits.get(doc.id)?.commands.length ?? 0;
  }

  /** Bo lenh cuoi cung. Undo o muc loi; Yjs UndoManager nam o tang tren. */
  undoLast(doc: DocumentId): boolean {
    const state = this.edits.get(doc.id);
    if (!state || state.commands.length === 0) return false;
    state.commands.pop();
    return true;
  }

  /**
   * Chay thu mot lan sua ma KHONG ghi nhan lenh.
   *
   * Giao dien can no de hien truoc canh bao "sẽ phải thay font" NGAY khi nguoi
   * dung dang go, chu khong phai sau khi da bam xac nhan.
   *
   * `scopeId` nhan ca ma khoi lan ma pham vi hep hon - xem edit/scope.ts.
   */
  async previewTextReplace(
    doc: DocumentId,
    scopeId: string,
    newText: string,
    options?: {
      align?: TextAlign;
      bold?: boolean;
      italic?: boolean;
      /** MA ho font (vd "noto-serif"). Ma khong hop le lui ve ho mac dinh. */
      fontFamily?: string;
      /** CO CHU, diem. Bo qua = giu co chu do duoc tu chu cu. */
      fontSizePt?: number;
      cover?: boolean;
    },
  ): Promise<Result<TextReplaceReport>> {
    const prepared = await this.prepareTextReplace(doc, scopeId, newText, {
      ...(options?.align ? { align: options.align } : {}),
      ...(options?.bold || options?.italic
        ? { face: { bold: options.bold === true, italic: options.italic === true } }
        : {}),
      ...(options?.fontFamily ? { family: normalizeFontFamilyId(options.fontFamily) } : {}),
      ...(options?.fontSizePt !== undefined ? { fontSizePt: options.fontSizePt } : {}),
      ...(options?.cover ? { cover: true } : {}),
    });
    if (!prepared.ok) return prepared;
    return ok(prepared.value);
  }

  // --- Anh da nhung san trong trang ----------------------------------------

  /**
   * DANH SACH ANH CUA MOT TRANG, da doi chieu HAI DUONG DOC.
   *
   * Duong thu nhat la danh sach toan tu cua pdf.js: no cho biet anh nam O DAU
   * tren giay. Duong thu hai la luong noi dung: no cho biet doan byte nao dat
   * anh o do. Chi khi hai duong dem ra CUNG mot so anh thi viec ghep cap theo
   * thu tu moi co co so - xem matchesOperatorList().
   *
   * `refusal` khac null = trang nay khong sua anh duoc, va chuoi do la cau tra
   * loi hien thang cho nguoi dung. Danh sach anh VAN duoc tra ve trong truong
   * hop do: nguoi dung van chon duoc de nhin thay no la mot doi tuong, chi la
   * khong keo duoc. Giau han di se lam cu bam cua ho thanh mot cu bam vao
   * khoang khong khong loi giai thich.
   */
  async pageImages(
    doc: DocumentId,
    pageIndex: number,
  ): Promise<Result<PageImagesInfo>> {
    const state = this.edits.get(doc.id);
    if (!state) {
      return fail('io-error', 'unknown document id', 'Tai lieu khong con mo.');
    }

    const cacheKey = doc.id + ':' + String(pageIndex);
    const cached = this.imageScans.get(cacheKey);
    if (cached) return cached;

    const task = (async (): Promise<Result<PageImagesInfo>> => {
      const proxy = await this.getPageProxy(doc, pageIndex);
      if (!proxy) {
        return fail(
          'page-out-of-range',
          'page proxy unavailable',
          'Khong mo duoc trang ' + String(pageIndex + 1) + '.',
        );
      }
      const scan = await scanPageForImages(proxy, pageIndex);

      const stream = await scanPageImages(state.originalBytes, pageIndex);
      if (!stream.ok) {
        return ok({ scan, occurrences: [], baselines: [], refusal: stream.reason });
      }
      const occurrences = imageDrawOrder(stream.scan, stream.imageNames);
      const refusal = matchesOperatorList(scan.images.length, occurrences.length, pageIndex);

      // GHEP CAP theo thu tu, va CHI khi hai he dem da khop. `refusal` khac null
      // nghia la chua khop - luc do khong co cap nao duoc dung ra, vi mot cap sai
      // se lam nguoi dung keo anh nay ma anh kia dich.
      const baselines: ImageBaseline[] = scan.images.map((img, i) => {
        const occurrence = refusal === null ? occurrences[i] : undefined;
        const block =
          occurrence === undefined
            ? undefined
            : stream.scan.editable.find((b) => b.occurrence === occurrence);
        return {
          pageIndex,
          occurrence: occurrence ?? -1,
          fullMatrix: img.fullMatrix,
          localMatrix: block?.localMatrix ?? null,
          transformRefusal: block?.clipped ? CLIPPED_IMAGE_REASON : null,
        };
      });

      return ok({ scan, occurrences, baselines, refusal: refusal ?? null });
    })();

    this.imageScans.set(cacheKey, task);
    return task;
  }

  /**
   * Kiem tra mot lenh anh TAI LUC NGUOI DUNG BAM.
   *
   * Cua chan thuc su nam trong scanImageDraws() - no la ben duy nhat doc duoc
   * luong noi dung va biet khoi q/Q co cat duoc khong. O day ta chi goi lai no
   * tren byte GOC va dich ket qua sang mot Result.
   */
  private async checkImageCommand(
    state: EditState,
    pageIndex: number,
    occurrence: number,
    /** true = di chuyen/co gian. Phep XOA khong vuong cua chan duong cat. */
    isTransform: boolean,
  ): Promise<Result<void>> {
    const stream = await scanPageImages(state.originalBytes, pageIndex);
    if (!stream.ok) {
      return fail('unsupported-feature', 'page content unreadable', stream.reason);
    }
    const block = stream.scan.editable.find((b) => b.occurrence === occurrence);
    if (block) {
      if (!stream.imageNames.has(block.name)) {
        return fail(
          'unsupported-feature',
          'xobject is not an image',
          'Doi tuong nay khong phai anh (/Subtype /Image). Ban web chi sua duoc anh.',
        );
      }
      // DUONG CAT chi chan DI CHUYEN. Xem CLIPPED_IMAGE_REASON: keo mot anh bi
      // cat ra khoi vung cat cua no cho ra mot tep hop le ma anh khong con hien
      // - cai hong duy nhat khong the phat hien duoc tu cau truc luong.
      if (isTransform && block.clipped) {
        return fail('unsupported-feature', 'image is inside a clipping path', CLIPPED_IMAGE_REASON);
      }
      return ok(undefined);
    }
    const why = stream.scan.refused.find((r) => r.occurrence === occurrence)?.reason;
    return fail(
      'unsupported-feature',
      'image draw not editable',
      why ?? 'Khong tim thay cho ve anh nay tren trang nua. Hay chon lai anh.',
    );
  }
}

export interface PageImagesInfo {
  scan: PageImageScan;
  /** imageIndex -> so thu tu toan tu Do trong luong noi dung. */
  occurrences: number[];
  /**
   * Anh nhu chung ton tai trong TEP GOC, da ghep cap hai duong doc.
   *
   * Rong khi `refusal` khac null: khong ghep cap duoc thi khong co du lieu goc
   * nao dung duoc, va tra ve mot danh sach ghep bua se de lop hien thi tuong la
   * no keo duoc.
   */
  baselines: ImageBaseline[];
  refusal: string | null;
}

/** Ma khoi co dang "p{trang}-b{so}". Tra ve null khi khong khop. */
function pageIndexFromBlockId(blockId: string): number | null {
  const m = /^p(\d+)-b\d+$/.exec(blockId);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isInteger(n) && n >= 0 ? n : null;
}
